import type { QuestionCommentModel } from "survey-core";
import { Textarea } from "@rumbledirect/ui-v2";
import { useSurveyQuestionRerender } from "./useSurveyQuestionRerender";

/**
 * Renderer for SurveyJS `comment` questions using admin-v2's `Textarea`.
 * Chrome (label, description, errors) lives in `SurveyQuestionChrome`; this
 * is only the control. Unlike `text`, comment questions commit the value on
 * every change (see `QuestionCommentModel.getTextAreaOptions`), so this is a
 * straightforward controlled write.
 *
 * The schema's `showCount` renders a `used/max` counter below the control.
 * `Textarea` has no built-in counter (a single-use presentational bit, not
 * worth promoting to the shared primitive for this one caller), so it is a
 * plain sibling element here.
 */
export function SurveyCommentQuestion({
  question,
}: {
  readonly question: QuestionCommentModel;
}) {
  useSurveyQuestionRerender(question);
  const value = question.value;
  const stringValue =
    value === undefined || value === null ? "" : String(value);
  const maxLength = question.getMaxLength();
  const showCount = question.getPropertyValue("showCount") === true;
  return (
    <div className="flex flex-col gap-1">
      <Textarea
        id={question.inputId}
        value={stringValue}
        placeholder={question.renderedPlaceholder ?? ""}
        rows={question.rows}
        disabled={question.isDisabledAttr}
        readOnly={question.isReadOnlyAttr}
        maxLength={maxLength > 0 ? maxLength : undefined}
        onChange={(event) => {
          question.value = event.target.value;
        }}
        onBlur={(event) => question.onBlur(event)}
        onFocus={(event) => question.onFocus(event)}
        aria-required={question.a11y_input_ariaRequired}
        aria-invalid={question.a11y_input_ariaInvalid}
        aria-describedby={question.a11y_input_ariaDescribedBy ?? undefined}
      />
      {showCount && maxLength > 0 && (
        <span className="self-end text-xs text-muted-foreground">
          {stringValue.length}/{maxLength}
        </span>
      )}
    </div>
  );
}
